import { type ReactNode, useEffect, useLayoutEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { useLocation } from 'wouter';
import { UnifiedSidebar } from '@/components/sidebar';
import { FinancesSidebar } from '@/components/sidebar/FinancesSidebar';
import { GlobalHeader } from '@/components/GlobalHeader';
import { ImpersonationBanner } from '@/components/ImpersonationBanner';
import { MobileTabBar } from '@/components/MobileTabBar';
import { UserMenu } from '@/components/sidebar/UserMenu';

interface FinancesLayoutProps {
  children: ReactNode;
}

/**
 * FinancesLayout provides the shell for all Finances module pages.
 *
 * Structure: GlobalHeader → [ UnifiedSidebar (primary) | FinancesSidebar (secondary) | content ]
 *
 * Mirrors MainLayout's auth handling but adds the Finances-specific
 * secondary sidebar (a la the Threads hover panel pattern, but persistent).
 */
export default function FinancesLayout({ children }: FinancesLayoutProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const { favicon } = useTheme();
  const [, navigate] = useLocation();
  const [location] = useLocation();
  // AR-210 (TC-12.1): every Finances route renders this same shell, so React
  // can keep the <main> scroller and its scrollTop between pages; Snapshot then
  // opened mid-page. Start each Finances page at the top.
  const mainRef = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    mainRef.current?.scrollTo({ top: 0, left: 0 });
  }, [location]);

  useEffect(() => {
    if (favicon) {
      const el = document.querySelector("link[rel='icon']") as HTMLLinkElement;
      if (el) {
        el.href = favicon;
      } else {
        const link = document.createElement('link');
        link.rel = 'icon';
        link.href = favicon;
        document.head.appendChild(link);
      }
    }
  }, [favicon]);

  const publicRoutes = ['/login', '/register', '/waiting-list', '/'];
  const authRoutes = ['/login', '/register'];
  const isPublicRoute = publicRoutes.includes(location);
  const isAuthRoute = authRoutes.includes(location);
  const shouldRedirect = !isAuthenticated && !isPublicRoute && !isLoading;

  useEffect(() => {
    if (shouldRedirect) {
      navigate('/login');
    }
  }, [shouldRedirect, navigate]);

  if (!isAuthenticated && !isPublicRoute) {
    if (isLoading) return <div>Loading...</div>;
    return null;
  }

  if (!isAuthenticated && isPublicRoute) {
    return <div className="min-h-screen bg-gray-50 dark:bg-[#101415]">{children}</div>;
  }

  if (isAuthRoute) {
    return <div className="min-h-screen bg-gray-50 dark:bg-[#101415]">{children}</div>;
  }

  return (
    <div
      data-finances-shell
      className="flex h-screen flex-col bg-gray-50 dark:bg-[#101415] overflow-hidden"
    >
      <GlobalHeader />
      <ImpersonationBanner />
      <div className="flex flex-1 min-h-0" data-finances-body>
        <div className="hidden md:flex">
          <UnifiedSidebar />
        </div>
        <div className="hidden md:flex">
          <FinancesSidebar />
        </div>
        <main
          ref={mainRef}
          data-snapshot-print-main
          className="flex-1 overflow-auto bg-white dark:bg-[#101415]"
        >
          {children}
        </main>
      </div>
      {/* AR-316 — the v9 phone shell: the two rails leave the flow, the primary
          destinations become a bottom tab bar, and the Finances second level
          moves into the More sheet above it. */}
      <MobileTabBar footer={<UserMenu expanded inline />}>
        {(close) => <FinancesSidebar inDrawer onNavigated={close} />}
      </MobileTabBar>
    </div>
  );
}
